import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { useSiteTooling } from '../hooks/useSiteTooling';

const Navigation = () => {
  const { trackEvent } = useSiteTooling();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const handleNavClick = (label, href) => {
    trackEvent('button_click', { button_text: label, location: 'navigation', href });
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'Products', href: '/products' },
    { label: 'Capabilities', href: '/capabilities' },
    { label: 'About', href: '/about' },
    { label: 'Projects', href: '/projects' },
    { label: 'Contact', href: '/contact' },
  ];

  const productLinks = [
    { label: 'Racking', href: '/products/racking' },
    { label: 'Shelving', href: '/products/shelving' },
    { label: 'Mezzanine', href: '/products/mezzanine' },
    { label: 'Storage', href: '/products/storage' },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 ${
          isScrolled
            ? 'bg-charcoal/90 backdrop-blur-md py-3'
            : 'bg-transparent py-4'
        }`}
      >
        <div className="w-full px-6 lg:px-12 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="/logo.webp" 
              alt="Molomo NR" 
              width="120"
              height="48"
              className={`transition-all duration-300 ${isScrolled ? 'h-10' : 'h-12'} w-auto`}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <Link to="/" onClick={() => handleNavClick('Home', '/')} className="text-sm text-white hover:text-white/90 transition-colors">Home</Link>

            {/* Products Dropdown */}
            <div className="relative group">
              <Link to="/products" onClick={() => handleNavClick('Products', '/products')} className="text-sm text-white hover:text-white/90 transition-colors">
                Products
              </Link>
              <div className="absolute top-full left-0 mt-2 w-48 bg-charcoal/95 backdrop-blur-md rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                {productLinks.map((link) => (
                  <Link
                    key={link.label}
                    to={link.href}
                    onClick={() => handleNavClick(link.label, link.href)}
                    className="block px-4 py-2 text-sm text-white hover:text-white hover:bg-white/10 transition-colors first:rounded-t-lg last:rounded-b-lg"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            <Link to="/capabilities" onClick={() => handleNavClick('Capabilities', '/capabilities')} className="text-sm text-white hover:text-white/90 transition-colors">Capabilities</Link>
            <Link to="/about" onClick={() => handleNavClick('About', '/about')} className="text-sm text-white hover:text-white/90 transition-colors">About</Link>
            <Link to="/projects" onClick={() => handleNavClick('Projects', '/projects')} className="text-sm text-white hover:text-white/90 transition-colors">Projects</Link>
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:+27829713299"
              onClick={() => handleNavClick('Phone', 'tel:+27829713299')}
              className="flex items-center gap-2 text-sm text-white hover:text-white/90 transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span>+27 82 971 3299</span>
            </a>
            <Link
              to="/contact"
              onClick={() => handleNavClick('Get a quote', '/contact')}
              className="bg-lime text-charcoal px-5 py-2.5 rounded-lg font-medium text-sm hover:bg-lime/90 transition-colors"
            >
              Get a quote
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div
        className={`fixed inset-0 z-[99] bg-charcoal transition-transform duration-300 lg:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col items-center min-h-full py-20 pb-32 overflow-y-auto">
          {/* Main Navigation */}
          <nav className="flex flex-col items-center gap-6 mb-8">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                onClick={() => handleNavClick(link.label, link.href)}
                className="text-3xl font-display text-white hover:text-lime transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Divider */}
          <div className="w-24 h-px bg-white/20 mb-8" />

          {/* Contact Section */}
          <div className="flex flex-col items-center gap-6 mt-auto">
            <a href="tel:+27829713299" className="flex items-center gap-2 text-white hover:text-lime transition-colors text-lg">
              <Phone className="w-5 h-5" />
              <span>+27 82 971 3299</span>
            </a>
            <Link to="/contact" className="bg-lime text-charcoal px-10 py-4 rounded-lg font-medium text-lg hover:bg-lime/90 transition-colors">
              Get a quote
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navigation;
